import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { createCroppedImageFile } from "./cropImage";

function ReferencePhotoCropModal({
  isOpen,
  imageSrc,
  fileName,
  mimeType,
  onClose,
  onConfirm,
  busy,
  confirmLabel = "Confirmar e subir foto"
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewStep, setPreviewStep] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setPreviewStep(false);
      if (preview?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(preview.previewUrl);
      }
      setPreview(null);
    }
  }, [isOpen, preview]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [busy, isOpen, onClose]);

  const gerarPreview = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      setPreviewLoading(true);

      if (preview?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(preview.previewUrl);
      }

      const nextPreview = await createCroppedImageFile({
        imageSrc,
        cropPixels: croppedAreaPixels,
        fileName: fileName || "foto-referencia.jpg",
        mimeType,
        rotation
      });

      setPreview(nextPreview);
      setPreviewStep(true);
    } catch (error) {
      onConfirm(null, error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmar = async () => {
    if (!preview?.file) return;
    await onConfirm(preview.file);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={!busy ? onClose : undefined}>
      <div
        className="modal-card crop-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reference-crop-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h3 id="reference-crop-title">Recortar foto de referência</h3>
            <div className="modal-subtitle">
              Ajuste a área para conter apenas o rosto ou a parte da imagem em que somente o aluno aparece.
            </div>
          </div>
          <button type="button" className="modal-close" aria-label="Fechar ajuste da foto" onClick={onClose} disabled={busy}>
            ×
          </button>
        </div>

        <div className="modal-body crop-modal-body">
          {!previewStep ? (
            <>
              <div className="crop-stage">
                <div className="cropper-wrapper">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={1}
                    cropShape="rect"
                    showGrid
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={setRotation}
                    onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                  />
                </div>

                <div className="crop-controls">
                  <div className="crop-control-group">
                    <label htmlFor="zoom-range">Zoom</label>
                    <div className="crop-control-input">
                      <input
                        id="zoom-range"
                        type="range"
                        min="1"
                        max="3"
                        step="0.05"
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                      />
                      <span>{zoom.toFixed(2)}x</span>
                    </div>
                  </div>

                  <div className="crop-control-group">
                    <label htmlFor="rotation-range">Rotação</label>
                    <div className="crop-control-input">
                      <input
                        id="rotation-range"
                        type="range"
                        min="-180"
                        max="180"
                        step="1"
                        value={rotation}
                        onChange={(e) => setRotation(Number(e.target.value))}
                      />
                      <span>{rotation}°</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="crop-actions">
                <button type="button" className="btn-limpar" onClick={onClose} disabled={busy || previewLoading}>
                  Cancelar
                </button>
                <button type="button" onClick={gerarPreview} disabled={busy || previewLoading || !croppedAreaPixels}>
                  {previewLoading ? "Gerando preview..." : "Gerar preview"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="crop-preview-stage">
                <div className="crop-preview-card">
                  <span className="crop-preview-label">Preview do recorte final</span>
                  <img src={preview?.previewUrl} alt="Preview do recorte" className="crop-preview-image" />
                </div>
              </div>

              <div className="crop-actions">
                <button
                  type="button"
                  className="btn-limpar"
                  onClick={() => setPreviewStep(false)}
                  disabled={busy}
                >
                  Voltar
                </button>
                <button type="button" onClick={confirmar} disabled={busy}>
                  {busy ? "Enviando..." : confirmLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReferencePhotoCropModal;
